/**
 * TeamStatus.js
 *
 * Displays a team's performance summary using modular card components.
 * Works for any library: stats are shown for each category attribute (e.g. Actor, Genre).
 */

import React from "react";
import TotalScoreCard from "../Cards/TotalScoreCard";
import ItemsBoughtCard from "../Cards/ItemsBoughtCard";
import AttributeStatsCard from "../Cards/AttributeStatsCard";
import TeamHeaderCard from "../Cards/TeamHeaderCard";
import TeamItemsCard from "../Cards/TeamItemsCard";
import AllTeamsItemsCard from "../Cards/AllTeamsItemsCard";
import RulesCard from "../Cards/RulesCard";
import NowOnAuctionCard from "../Cards/NowOnAuctionCard";
import { getAuctionSettings } from "../assets/Rules";
import { getTeamColor } from "../assets/teamColors";
import { getCategoryAttributes, getRoomLibrary, getTeamItems } from "../library/libraries";

const TeamStatus = ({ team, allTeams, room }) => {
    const { name, budgetLeft } = team || {};
    const settings = getAuctionSettings(room);
    const library = getRoomLibrary(room);
    const items = getTeamItems(team);

    const totalScore = items.reduce((sum, item) => sum + (Number(item.score) || 0), 0);

    return (
        <div className="w-full max-w-5xl mx-auto px-6 py-10 flex flex-col gap-8 text-zinc-100 mt-12">

            {/* --- Team Header --- */}
            <TeamHeaderCard
                name={name}
                color={getTeamColor(team)}
                budgetLeft={budgetLeft}
            />

            {/* --- What the host has up right now --- */}
            {room && <NowOnAuctionCard room={room} />}

            {/* --- Metric Cards Grid --- */}
            <section className="grid gap-4 grid-cols-2 [&>*]:h-full">
                <TotalScoreCard totalScore={totalScore} scoreAttribute={library.scoreAttribute} />
                <ItemsBoughtCard totalItems={items.length} maxItems={settings.maxItemsPerTeam} />
            </section>

            <TeamItemsCard items={items} scoreAttribute={library.scoreAttribute} />

            {/* --- Stats per category attribute --- */}
            {getCategoryAttributes(library).map((attribute) => (
                <AttributeStatsCard
                    key={attribute.name}
                    attribute={attribute}
                    items={items}
                    rule={settings.attributeRules[attribute.name]}
                />
            ))}

            {settings.showInventories && <AllTeamsItemsCard allTeams={allTeams} settings={settings} library={library} />}
            <RulesCard settings={settings} library={library} />
        </div>
    );
};

export default TeamStatus;
